import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, AuthCallback } from "@/components/auth";
import { BookingProvider } from "@/components/booking";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import MobileBar from "@/components/MobileBar";
import Home from "@/pages/Home";
import Services from "@/pages/Services";
import Bridal from "@/pages/Bridal";
import About from "@/pages/About";
import Gallery from "@/pages/Gallery";
import Visit from "@/pages/Visit";
import Account from "@/pages/Account";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import ChatWidget from "@/components/ChatWidget";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ScrollAndSmooth() {
  const location = useLocation();

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  useEffect(() => {
    if (!location.hash) {
      window.__lenis ? window.__lenis.scrollTo(0, { immediate: true }) : window.scrollTo(0, 0);
    }
  }, [location.pathname, location.hash]);

  return null;
}

function Tracker() {
  const location = useLocation();
  useEffect(() => {
    if (location.pathname.startsWith("/admin")) return;
    let vid = localStorage.getItem("gg_vid");
    if (!vid) {
      vid = crypto.randomUUID();
      localStorage.setItem("gg_vid", vid);
    }
    const w = window.innerWidth;
    const device = w < 768 ? "mobile" : w < 1024 ? "tablet" : "desktop";
    fetch(`${API}/track`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ path: location.pathname, referrer: document.referrer, device, vid }),
    }).catch(() => {});
  }, [location.pathname]);
  return null;
}

function AppRouter() {
  const location = useLocation();
  // Detect the OAuth session_id synchronously during render to avoid race conditions
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  if (location.pathname.startsWith("/admin")) {
    return (
      <Routes>
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/admin/*" element={<AdminDashboard />} />
      </Routes>
    );
  }
  return (
    <>
      <Tracker />
      <ChatWidget />
      <Header />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/services" element={<Services />} />
        <Route path="/bridal-makeup" element={<Bridal />} />
        <Route path="/about" element={<About />} />
        <Route path="/gallery" element={<Gallery />} />
        <Route path="/visit" element={<Visit />} />
        <Route path="/account" element={<Account />} />
        <Route path="*" element={<Home />} />
      </Routes>
      <Footer />
      <MobileBar />
      <div className="h-16 md:hidden" aria-hidden="true" />
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <BookingProvider>
            <ScrollAndSmooth />
            <AppRouter />
            <Toaster position="top-center" richColors />
          </BookingProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
